import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Minus, ShoppingCart, Trash2, Printer, X, User, Clock, AlertCircle, CreditCard, Image, Edit2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { GuestLink, ReservationLink, SaleLink } from '../components/crm/EntityLinks';
import { formatCurrency } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SalesPage() {
  const { can, user } = useAuth();
  const riad = user?.current_riad || {};
  const riadName = riad.name || 'Mon Établissement';
  const riadLogo = riad.logo ? `${API_URL}/uploads/${riad.logo}` : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [sales, setSales] = useState([]);
  const [pendingSales, setPendingSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayStats, setTodayStats] = useState({ total: 0, count: 0, pendingCount: 0 });
  const [view, setView] = useState('pos');
  
  // Client selection
  const [clientType, setClientType] = useState('RESERVATION');
  const [currentReservation, setCurrentReservation] = useState(null);
  const [recentReservations, setRecentReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all'); // Category filter for POS
  
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Edit/Delete state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSaleId, setDeletingSaleId] = useState(null);
  
  // Confirmation popup for checkout
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [pendingCheckoutStatus, setPendingCheckoutStatus] = useState(null);

  // Custom product dialog
  const [showCustomProduct, setShowCustomProduct] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductPrice, setCustomProductPrice] = useState('');
  const [allowCustomProduct, setAllowCustomProduct] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchTodaySales();
    fetchSalesHistory();
    fetchPendingSales();
    fetchCurrentReservation();
  }, []);

  // Handle URL parameter for direct navigation to a sale
  useEffect(() => {
    const saleId = searchParams.get('id') || searchParams.get('sale_id');
    if (saleId) {
      // First try to find in loaded sales and use getTicket to get proper format
      const fetchTicket = async () => {
        try {
          const response = await api.get(`/sales/${saleId}/ticket`);
          if (response.data) {
            setSelectedTicket(response.data);
            setShowTicketModal(true);
            setView('history');
            setSearchParams({});
          }
        } catch (error) {
          console.error('Failed to fetch sale ticket:', error);
        }
      };
      fetchTicket();
    }
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products', { params: { active: true, limit: 100 } });
      setProducts(response.data.data);
      // Check if custom product is enabled via tenant settings
      try {
        const settingsResp = await api.get('/settings');
        const posFreeSetting = settingsResp.data?.pos_free_product_enabled;
        setAllowCustomProduct(posFreeSetting === true || posFreeSetting === 'true');
      } catch (e) {
        // Fallback: try old config method
        const configResp = await api.get('/config/select', { params: { categories: 'POS' } });
        const posConfig = configResp.data?.POS || [];
        const customOpt = posConfig.find(o => o.value === 'ALLOW_CUSTOM_PRODUCT');
        setAllowCustomProduct(customOpt?.metadata?.enabled || false);
      }
    } catch (error) {
      console.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentReservation = async () => {
    try {
      const response = await api.get('/sales/current-reservation');
      setCurrentReservation(response.data.current);
      setRecentReservations(response.data.recent || []);
      if (response.data.current) {
        setSelectedReservation(response.data.current);
      }
    } catch (error) {
      console.error('Failed to fetch current reservation');
    }
  };

  const fetchTodaySales = async () => {
    try {
      const response = await api.get('/sales/today');
      setTodayStats({ 
        total: response.data.total, 
        count: response.data.count,
        pendingCount: response.data.pendingCount || 0
      });
    } catch (error) {
      console.error('Failed to fetch today sales');
    }
  };

  const fetchSalesHistory = async () => {
    try {
      const response = await api.get('/sales', { params: { limit: 50 } });
      setSales(response.data.data);
    } catch (error) {
      console.error('Failed to fetch sales');
    }
  };

  const fetchPendingSales = async () => {
    try {
      const response = await api.get('/sales/pending');
      // Map lines to items for frontend consistency
      const salesWithItems = response.data.map(sale => ({
        ...sale,
        items: sale.lines || []
      }));
      setPendingSales(salesWithItems);
    } catch (error) {
      console.error('Failed to fetch pending sales');
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      if (product.type === 'PRODUCT' && existing.quantity >= product.stock_quantity) {
        toast.error('Stock insuffisant');
        return;
      }
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      if (product.type === 'PRODUCT' && product.stock_quantity < 1) {
        toast.error('Produit en rupture de stock');
        return;
      }
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        product_image: product.image_url,
        quantity: 1,
        unit_price: product.sell_price,
        buy_price: product.buy_price,
        type: product.type,
        max_stock: product.stock_quantity
      }]);
    }
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty < 1) return item;
        if (item.type === 'PRODUCT' && newQty > item.max_stock) {
          toast.error('Stock insuffisant');
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  // Add custom product to cart
  const addCustomProduct = () => {
    if (!customProductName.trim() || !customProductPrice) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    const price = parseFloat(customProductPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Prix invalide');
      return;
    }
    const customId = `custom-${Date.now()}`;
    setCart([...cart, {
      product_id: customId,
      product_name: customProductName.trim(),
      name: customProductName.trim(),
      quantity: 1,
      unit_price: price,
      buy_price: 0,
      type: 'SERVICE',
      is_custom: true
    }]);
    setShowCustomProduct(false);
    setCustomProductName('');
    setCustomProductPrice('');
    toast.success('Produit ajouté');
  };

  // Show confirmation popup before checkout
  const confirmCheckout = (status = 'PAID') => {
    if (cart.length === 0) {
      toast.error('Le panier est vide');
      return;
    }
    setPendingCheckoutStatus(status);
    setShowCheckoutConfirm(true);
  };

  const handleCheckout = async (status = 'PAID') => {
    setShowCheckoutConfirm(false);
    
    if (cart.length === 0) {
      toast.error('Le panier est vide');
      return;
    }

    try {
      const saleData = {
        lines: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name || item.name, // Include product name for custom products
          quantity: item.quantity,
          unit_price: item.unit_price,
          buy_price: item.buy_price
        })),
        status,
        client_type: clientType,
        reservation_id: clientType === 'RESERVATION' && selectedReservation ? selectedReservation.id : null,
        guest_id: clientType === 'RESERVATION' && selectedReservation?.guest ? selectedReservation.guest.id : null
      };

      await api.post('/sales', saleData);

      toast.success(status === 'PAID' ? 'Vente enregistrée' : 'Ticket mis en attente');
      setCart([]);
      fetchProducts();
      fetchTodaySales();
      fetchSalesHistory();
      fetchPendingSales();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la vente');
    }
  };

  const handlePayPending = async (saleId) => {
    try {
      await api.put(`/sales/${saleId}/pay`);
      toast.success('Ticket encaissé');
      fetchTodaySales();
      fetchSalesHistory();
      fetchPendingSales();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const printTicket = async (saleId) => {
    try {
      const response = await api.get(`/sales/${saleId}/ticket`);
      setSelectedTicket(response.data);
      setShowTicketModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du ticket');
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const logoHtml = riadLogo 
      ? `<img src="${riadLogo}" alt="${riadName}" style="max-height: 60px; margin-bottom: 10px;" />`
      : '';
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ticket ${selectedTicket.ticket_number}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          body { font-family: Arial, sans-serif; max-width: 210mm; margin: 0 auto; padding: 20mm; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #047857; padding-bottom: 20px; }
          .header img { max-height: 60px; }
          .header h1 { color: #047857; margin: 0; font-size: 28px; }
          .header .subtitle { color: #666; margin-top: 5px; }
          .info { display: flex; justify-content: space-between; margin: 20px 0; }
          .info p { margin: 5px 0; }
          table { width: 100%; border-collapse: collapse; margin: 30px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f8f9fa; font-weight: 600; }
          .text-right { text-align: right; }
          .total-row { font-size: 1.2em; font-weight: bold; border-top: 2px solid #333; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          ${logoHtml}
          <h1>${riadName}</h1>
          <p class="subtitle">Ticket de vente</p>
        </div>
        <div class="info">
          <div>
            <p><strong>N° Ticket:</strong> ${selectedTicket.ticket_number}</p>
            <p><strong>Client:</strong> ${selectedTicket.guest}</p>
          </div>
          <div class="text-right">
            <p><strong>Date:</strong> ${format(new Date(selectedTicket.date), 'dd/MM/yyyy')}</p>
            <p><strong>Heure:</strong> ${format(new Date(selectedTicket.date), 'HH:mm')}</p>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Désignation</th>
              <th class="text-right">Qté</th>
              <th class="text-right">Prix unit.</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${selectedTicket.lines.map(line => `
              <tr>
                <td>${line.product}</td>
                <td class="text-right">${line.quantity}</td>
                <td class="text-right">${line.unit_price} ${selectedTicket.currency}</td>
                <td class="text-right">${line.total} ${selectedTicket.currency}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="3">TOTAL</td>
              <td class="text-right">${selectedTicket.total} ${selectedTicket.currency}</td>
            </tr>
          </tbody>
        </table>
        <p><strong>Mode de paiement:</strong> Espèces</p>
        <div class="footer">
          <p>Merci de votre visite !</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Edit sale
  const handleEditSale = async (sale) => {
    try {
      const response = await api.get(`/sales/${sale.id}`);
      setEditingSale(response.data);
      setShowEditModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de la vente');
    }
  };

  const handleUpdateSale = async () => {
    if (!editingSale) return;
    try {
      await api.put(`/sales/${editingSale.id}`, { notes: editingSale.notes });
      toast.success('Vente mise à jour');
      setShowEditModal(false);
      setEditingSale(null);
      fetchSalesHistory();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  // Delete sale
  const handleDeleteSale = async () => {
    if (!deletingSaleId) return;
    try {
      await api.delete(`/sales/${deletingSaleId}`);
      toast.success('Vente supprimée');
      setShowDeleteConfirm(false);
      setDeletingSaleId(null);
      fetchTodaySales();
      fetchSalesHistory();
      fetchPendingSales();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Ventes</h1>
          <p className="text-muted-foreground mt-1">Point de vente</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Ventes aujourd'hui</p>
            <p className="text-xl font-bold text-primary">{formatCurrency(todayStats.total)}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={view === 'pos' ? 'default' : 'outline'}
              onClick={() => setView('pos')}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Caisse
            </Button>
            <Button
              variant={view === 'history' ? 'default' : 'outline'}
              onClick={() => setView('history')}
            >
              Historique
            </Button>
          </div>
        </div>
      </div>

      {/* Pending Sales Alert */}
      {pendingSales.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <p className="font-medium text-orange-800">
                {pendingSales.length} ticket(s) en attente de paiement
              </p>
            </div>
            <div className="space-y-2">
              {pendingSales.map((sale) => (
                <div key={sale.id} className="bg-white p-3 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold">{sale.sale_number}</span>
                      <span className="font-semibold text-orange-700">{formatCurrency(sale.total)}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handlePayPending(sale.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`pay-pending-${sale.id}`}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        Encaisser
                      </Button>
                    </div>
                  </div>
                  {/* Items summary */}
                  <div className="text-sm text-gray-600 space-y-1">
                    {sale.items?.slice(0, 3).map((item, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{item.product_name || item.product?.name} × {item.quantity}</span>
                        <span>{formatCurrency(item.quantity * item.unit_price)}</span>
                      </div>
                    ))}
                    {sale.items?.length > 3 && (
                      <div className="text-gray-400">+ {sale.items.length - 3} autre(s)</div>
                    )}
                  </div>
                  {/* Client/Reservation info */}
                  <div className="mt-2 pt-2 border-t border-orange-100 flex flex-wrap gap-2 text-xs">
                    {sale.guest && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <User className="w-3 h-3" />
                        {sale.guest.first_name} {sale.guest.last_name}
                      </span>
                    )}
                    {sale.reservation && (
                      <ReservationLink reservation={sale.reservation} showIcon={true} />
                    )}
                    {!sale.guest && !sale.reservation && (
                      <span className="text-gray-400">Client passage</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {view === 'pos' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products Grid */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3">
                  <CardTitle>Produits & Services</CardTitle>
                  {/* Category filter buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilterCategory('all')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all
                        ${filterCategory === 'all' 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      Tous
                    </button>
                    {[...new Map(products.filter(p => p.category).map(p => [p.category?.id, p.category])).values()].map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setFilterCategory(cat.id.toString())}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5
                          ${filterCategory === cat.id.toString() 
                            ? 'text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        style={filterCategory === cat.id.toString() ? { backgroundColor: cat.color || '#6366f1' } : {}}
                      >
                        <span 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: cat.color || '#6366f1' }}
                        />
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {products
                      .filter(p => filterCategory === 'all' || p.category?.id?.toString() === filterCategory)
                      .map((product) => (
                      <button
                        key={product.id}
                        onClick={() => addToCart(product)}
                        disabled={product.type === 'PRODUCT' && product.stock_quantity < 1}
                        className={`p-3 rounded-xl border text-left transition-all hover:border-primary hover:shadow-md relative overflow-hidden
                          ${product.type === 'PRODUCT' && product.stock_quantity < 1 ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        data-testid={`product-btn-${product.id}`}
                      >
                        {product.image_url ? (
                          <div className="w-full h-20 mb-2 rounded-lg overflow-hidden bg-gray-100">
                            <img 
                              src={`${API_URL}${product.image_url}`} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        ) : (
                          <div className="w-full h-20 mb-2 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Image className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <p className="text-primary font-bold mt-1">{formatCurrency(product.sell_price)}</p>
                        {product.type === 'PRODUCT' && (
                          <p className={`text-xs mt-1 ${product.stock_quantity < 5 ? 'text-red-500' : 'text-muted-foreground'}`}>
                            Stock: {product.stock_quantity}
                          </p>
                        )}
                        {product.type === 'SERVICE' && (
                          <Badge variant="outline" className="text-xs mt-1">Service</Badge>
                        )}
                      </button>
                    ))}
                    {/* Custom product button */}
                    {allowCustomProduct && (
                      <button
                        onClick={() => setShowCustomProduct(true)}
                        className="p-3 rounded-xl border-2 border-dashed border-gray-300 text-left transition-all hover:border-primary hover:shadow-md flex flex-col items-center justify-center min-h-[140px]"
                        data-testid="custom-product-btn"
                      >
                        <Plus className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="font-medium text-sm text-gray-500">Produit libre</p>
                        <p className="text-xs text-gray-400">Montant personnalisé</p>
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cart */}
          <div>
            <Card className="sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Panier
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Client Selection */}
                <div className="mb-4 p-3 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Client</span>
                  </div>
                  <Select value={clientType} onValueChange={setClientType}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RESERVATION">Réservation</SelectItem>
                      <SelectItem value="WALK_IN">Client de passage</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {clientType === 'RESERVATION' && (
                    <Select 
                      value={selectedReservation?.id?.toString() || ''} 
                      onValueChange={(v) => {
                        const resa = recentReservations.find(r => r.id.toString() === v);
                        setSelectedReservation(resa);
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Sélectionner une réservation" />
                      </SelectTrigger>
                      <SelectContent>
                        {currentReservation && (
                          <SelectItem value={currentReservation.id.toString()}>
                            <span className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-800 text-xs">En cours</Badge>
                              {currentReservation.guest?.first_name} {currentReservation.guest?.last_name}
                            </span>
                          </SelectItem>
                        )}
                        {recentReservations.filter(r => r.id !== currentReservation?.id).map((resa) => (
                          <SelectItem key={resa.id} value={resa.id.toString()}>
                            {resa.guest?.first_name} {resa.guest?.last_name} - {format(new Date(resa.check_in), 'dd/MM')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {cart.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Panier vide
                  </p>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.product_id} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.product_name}</p>
                          <p className="text-sm text-primary font-semibold">
                            {formatCurrency(item.unit_price * item.quantity)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="w-7 h-7"
                            onClick={() => updateQuantity(item.product_id, -1)}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="w-7 h-7"
                            onClick={() => updateQuantity(item.product_id, 1)}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7"
                          onClick={() => removeFromCart(item.product_id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}

                    <div className="pt-4 border-t space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Total</span>
                        <span className="text-2xl font-bold text-primary">
                          {formatCurrency(getTotal())}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        Paiement espèces uniquement • Sans TVA
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => confirmCheckout('PENDING')}
                          data-testid="pending-btn"
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          En attente
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={() => confirmCheckout('PAID')}
                          data-testid="checkout-btn"
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Encaisser
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        /* Sales History */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="text-left p-3">N° Ticket</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Client</th>
                    <th className="text-left p-3">Articles</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Statut</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-t" data-testid={`sale-row-${sale.id}`}>
                      <td className="p-3 font-mono text-sm">
                        <SaleLink sale={sale} showIcon={false} />
                      </td>
                      <td className="p-3">{format(new Date(sale.sale_date), 'dd/MM/yyyy HH:mm')}</td>
                      <td className="p-3">
                        {sale.client_type === 'WALK_IN' ? (
                          <Badge variant="outline">Client passage</Badge>
                        ) : sale.guest ? (
                          <GuestLink guest={sale.guest} />
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="p-3">{sale.lines?.length || 0} article(s)</td>
                      <td className="p-3 font-semibold">{formatCurrency(sale.total)}</td>
                      <td className="p-3">
                        <Badge className={sale.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                          {sale.status === 'PAID' ? 'Payé' : 'En attente'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {sale.status === 'PENDING' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePayPending(sale.id)}
                              className="text-green-600"
                            >
                              Encaisser
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => printTicket(sale.id)}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          {can('sales.edit') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditSale(sale)}
                              data-testid={`edit-sale-${sale.id}`}
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                          {can('sales.delete') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setDeletingSaleId(sale.id); setShowDeleteConfirm(true); }}
                              data-testid={`delete-sale-${sale.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Preview Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Aperçu du ticket</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="text-center pb-4 border-b">
                {riadLogo && <img src={riadLogo} alt={riadName} className="h-12 mx-auto mb-2" />}
                <h2 className="text-xl font-bold text-primary">{riadName}</h2>
                <p className="text-sm text-muted-foreground">Ticket de vente</p>
              </div>
              <div className="flex justify-between text-sm">
                <div>
                  <p><strong>N°:</strong> {selectedTicket.ticket_number}</p>
                  <p><strong>Client:</strong> {selectedTicket.guest}</p>
                </div>
                <div className="text-right">
                  <p>{format(new Date(selectedTicket.date), 'dd/MM/yyyy')}</p>
                  <p>{format(new Date(selectedTicket.date), 'HH:mm')}</p>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Article</th>
                    <th className="text-right py-2">Qté</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTicket.lines.map((line, i) => (
                    <tr key={i} className="border-b border-dashed">
                      <td className="py-2">{line.product}</td>
                      <td className="text-right py-2">{line.quantity}</td>
                      <td className="text-right py-2">{line.total} {selectedTicket.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center pt-2 border-t-2">
                <span className="font-bold text-lg">TOTAL</span>
                <span className="font-bold text-lg text-primary">{selectedTicket.total} {selectedTicket.currency}</span>
              </div>
              <Button className="w-full" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Imprimer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Sale Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier la vente</DialogTitle>
          </DialogHeader>
          {editingSale && (
            <div className="space-y-4">
              <div className="text-sm">
                <p><strong>N° Ticket:</strong> {editingSale.sale_number}</p>
                <p><strong>Total:</strong> {formatCurrency(editingSale.total)}</p>
                <p><strong>Statut:</strong> {editingSale.status === 'PAID' ? 'Payé' : 'En attente'}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Input
                  value={editingSale.notes || ''}
                  onChange={(e) => setEditingSale({ ...editingSale, notes: e.target.value })}
                  placeholder="Notes sur cette vente..."
                />
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm font-medium mb-2">Articles</p>
                {editingSale.lines?.map((line, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                    <span>{line.product_name} × {line.quantity}</span>
                    <span>{formatCurrency(line.line_total)}</span>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
                <Button onClick={handleUpdateSale}>Enregistrer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Êtes-vous sûr de vouloir supprimer cette vente ? Le stock des produits sera restauré et le mouvement de caisse sera annulé.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDeleteSale}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Confirmation Modal */}
      <Dialog open={showCheckoutConfirm} onOpenChange={setShowCheckoutConfirm}>
        <DialogContent className="max-w-md" data-testid="checkout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Confirmer l'encaissement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Récapitulatif :</p>
              <div className="space-y-1">
                {cart.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{item.product_name || item.name} × {item.quantity}</span>
                    <span>{formatCurrency(item.quantity * item.unit_price)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-3 pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-primary">{formatCurrency(getTotal())}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {pendingCheckoutStatus === 'PAID' 
                ? 'Cette vente sera enregistrée comme payée et le mouvement de caisse sera créé.'
                : 'Cette vente sera mise en attente de paiement.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckoutConfirm(false)}>Annuler</Button>
            <Button 
              onClick={() => handleCheckout(pendingCheckoutStatus)}
              data-testid="confirm-checkout-btn"
            >
              {pendingCheckoutStatus === 'PAID' ? 'Confirmer l\'encaissement' : 'Mettre en attente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Product Dialog */}
      <Dialog open={showCustomProduct} onOpenChange={setShowCustomProduct}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Produit libre</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={customProductName}
                onChange={(e) => setCustomProductName(e.target.value)}
                placeholder="Ex: Service divers, Supplément..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Prix (MAD)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={customProductPrice}
                onChange={(e) => setCustomProductPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomProduct(false)}>Annuler</Button>
            <Button onClick={addCustomProduct}>Ajouter au panier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
